import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { NavBar } from 'components/NavBar/NavBar'
import {
  Button,
  Flex,
  MainHeadline,
  PageWrapper,
} from 'styling/GlobalStyles'
import {
  BodyText,
  Heading3,
  OtherText,
  ParagraphText,
} from 'styling/Typography'
import {
  HeaderRow,
  HeaderActions,
  KpiGrid,
  KpiCard,
  KpiValue,
  DashGrid,
  Panel,
  PanelHeader,
  CompositionGrid,
  CompositionRow,
  Divider,
  TotalRow,
  UtilRow,
  UtilBar,
  UtilFill,
  UtilMark,
  ComingSoonWrapper,
  fleetColors,
} from './FleetOverview.styled'
import {
  getFleetDashboardOverview,
  getFleetCategories,
  getFleetCategoryUtilization,
} from 'features/machinery/fleetMachinesSlice'

const TruckIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}>
    <path d='M3 7h11v8H3z' />
    <path d='M14 10h4l3 3v2h-7z' />
    <circle cx='7' cy='17' r='2' />
    <circle cx='17' cy='17' r='2' />
  </svg>
)

const TrendIcon = ({ color }: { color: string }) => (
  <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke={color} strokeWidth={2.5} strokeLinecap='round' strokeLinejoin='round'>
    <path d='M3 17l6-6 4 4 8-8' />
    <path d='M14 7h7v7' />
  </svg>
)

const ComingSoon = ({ children }: { children: React.ReactNode }) => (
  <ComingSoonWrapper>
    <div className='cs-blur'>{children}</div>
    <div className='cs-label'>Coming Soon</div>
  </ComingSoonWrapper>
)

export const FleetOverview: React.FC = () => {
  const navigate = useNavigate()
  const theme: any = useTheme()
  const dispatch = useDispatch<AppDispatch>()

  const dashboardOverview = useSelector((state: RootState) => state.fleetMachines.dashboardOverview)
  const categories = useSelector((state: RootState) => state.fleetMachines.categories)
  const categoryUtilization = useSelector((state: RootState) => state.fleetMachines.categoryUtilization)

  useEffect(() => {
    dispatch(getFleetDashboardOverview())
    dispatch(getFleetCategories())
    dispatch(getFleetCategoryUtilization())
  }, [])

  const kpiCards = [
    {
      label: 'Availability',
      value: dashboardOverview ? `${dashboardOverview.availabilityPercentage}%` : '—',
      sub: `${dashboardOverview?.operationalMachineCount ?? '—'} of ${dashboardOverview?.totalMachineCount ?? '—'} operational`,
      accent: fleetColors.success,
      trend: true,
    },
    {
      label: 'Avg Utilization (24h)',
      value: dashboardOverview ? `${dashboardOverview.utilizationPercentage}%` : '—',
      sub: 'vs target 70%',
      accent: theme.colorOrange,
    },
    // MTBF — not in response
    // Cost per Op-Hour — not in response
  ]

  const statCards = [
    {
      label: 'In Use Now',
      value: dashboardOverview?.inUseCount ?? '—',
      sub: `${dashboardOverview?.operationalMachineCount ?? '—'} operational`,
      accent: theme.colorOrange,
    },
    {
      label: 'Under Maintenance',
      value: dashboardOverview?.inMaintenanceCount ?? '—',
      sub: '',
      accent: theme.textColor,
    },
    {
      label: 'Broken/Critical',
      value: dashboardOverview?.brokenCount ?? '—',
      sub: '',
      accent: fleetColors.danger,
    },
    {
      label: 'Low Fuel',
      value: dashboardOverview?.lowFuelPercentageCount ?? '—',
      sub: 'machines below 25%',
      accent: fleetColors.yellow,
    },
  ]

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <HeaderRow>
          <MainHeadline>Fleet Overview</MainHeadline>
          <HeaderActions>
          </HeaderActions>
        </HeaderRow>

        <KpiGrid style={{ gridTemplateColumns: `repeat(${kpiCards.length}, 1fr)` }}>
          {kpiCards.map((k) => (
            <KpiCard key={k.label} accent={k.accent}>
              <ParagraphText grey weight='600' style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {k.label}
              </ParagraphText>
              <KpiValue accent={k.accent}>{k.value}</KpiValue>
              <Flex gap='0.35rem' center>
                {k.trend && <TrendIcon color={k.accent} />}
                <OtherText grey>{k.sub}</OtherText>
              </Flex>
            </KpiCard>
          ))}
        </KpiGrid>

        <KpiGrid>
          {statCards.map((k) => (
            <KpiCard key={k.label} accent={k.accent}>
              <ParagraphText grey weight='600' style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {k.label}
              </ParagraphText>
              <KpiValue accent={k.accent} size={28}>{String(k.value)}</KpiValue>
              {k.sub ? <OtherText grey>{k.sub}</OtherText> : null}
            </KpiCard>
          ))}
        </KpiGrid>

        <DashGrid>
          <Panel>
            <PanelHeader>
              <Heading3 weight='700' style={{ margin: 0, fontSize: 18 }}>Fleet Composition</Heading3>
              <span className='view-all' onClick={() => navigate('/machinery/fleet')}>
                View all ›
              </span>
            </PanelHeader>
            <CompositionGrid>
              {categories.slice(0, 6).map((c) => (
                <CompositionRow key={c.id}>
                  <div className='left'>
                    <div className='icon-wrap'><TruckIcon /></div>
                    <div>
                      <BodyText weight='700' style={{ margin: 0 }}>{c.name}</BodyText>
                    </div>
                  </div>
                  <span className='count'>{c.machineCount}</span>
                </CompositionRow>
              ))}
            </CompositionGrid>
          </Panel>

          <ComingSoon>
            <Panel>
              <PanelHeader>
                <Heading3 weight='700' style={{ margin: 0, fontSize: 18 }}>Cost Breakdown (last 7d)</Heading3>
              </PanelHeader>
              {[
                { label: 'Fuel', amount: '$84.2k', pct: 42 },
                { label: 'Maintenance & Parts', amount: '$51.8k', pct: 26 },
                { label: 'Operator Labor', amount: '$39.6k', pct: 20 },
                { label: 'Depreciation', amount: '$24.6k', pct: 12 },
              ].map((c) => (
                <div key={c.label} style={{ padding: '0.6rem 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <BodyText style={{ margin: 0 }}>{c.label}</BodyText>
                    <span>{c.amount} <span style={{ opacity: 0.5 }}>{c.pct}%</span></span>
                  </div>
                </div>
              ))}
              <Divider />
              <TotalRow>
                <BodyText grey style={{ margin: 0 }}>Total fleet cost</BodyText>
                <strong>$200.0k</strong>
              </TotalRow>
            </Panel>
          </ComingSoon>
        </DashGrid>

        <DashGrid>
          <Panel>
            <PanelHeader>
              <div>
                <Heading3 weight='700' style={{ margin: 0, fontSize: 18 }}>Utilization by Category</Heading3>
                <OtherText grey style={{ marginTop: 4 }}>Last 24h · white bar = 70% target</OtherText>
              </div>
            </PanelHeader>
            {categoryUtilization.map((u) => {
              const pct = u.utilizationPercentage ?? 0
              const color = pct >= 70 ? fleetColors.success : theme.colorOrange
              return (
                <UtilRow key={u.id}>
                  <div className='label-row'>
                    <BodyText style={{ margin: 0 }}>{u.name}</BodyText>
                    <span className='value' style={{ color }}>
                      {pct}%<span className='target'>/ 70%</span>
                    </span>
                  </div>
                  <UtilBar>
                    <UtilFill width={pct} color={color} />
                    <UtilMark position={70} />
                  </UtilBar>
                </UtilRow>
              )
            })}
          </Panel>

          <ComingSoon>
            <Panel>
              <PanelHeader>
                <Heading3 weight='700' style={{ margin: 0, fontSize: 18 }}>Active Alerts</Heading3>
              </PanelHeader>
              {[1, 2, 3].map((i) => (
                <div key={i} style={{ padding: '0.75rem 0', borderBottom: `1px solid ${fleetColors.divider}` }}>
                  <BodyText style={{ margin: 0 }}>Alert placeholder {i}</BodyText>
                </div>
              ))}
            </Panel>
          </ComingSoon>
        </DashGrid>
      </PageWrapper>
    </>
  )
}
